import { promises } from "dns";
import apiClient from ".";
import { ConversationStatus, IChatHistory, IConversation, IMessage, ObjectId } from "../models";
import { IAgent } from "../models/agent";
import { IFAQ } from "../models/faq";
import { IFile } from "../models/file";
import { ITextBlock } from "../models/ITextblock";
import { IWebLink } from "../models/webLink";
import { APIToolConfig, PredefinedTool } from "../components/Configure/ToolsConfiguration";

// crud methods for Agentanization
export const createAgent = async (body: {
	name: string, 
	description: string, 
	orgId: ObjectId, 
	isPrivate: boolean
}) => {
	const response = apiClient.post<IAgent>(`/orgs/${body.orgId}/agents`, body);
	return response;
}
export const getAgent = async (body: {orgId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.get<IAgent>(`/orgs/${body.orgId}/agents/${body.id}`);
	return response.data;
};
export const getAgents = async (orgId: ObjectId) => {
	const response = await apiClient.get<IAgent[]>(`/orgs/${orgId}/agents`);
	return response.data;
}
export const updateAgent = async (body: {
	id: ObjectId,
	orgId: ObjectId,
	
	name?: string,
	description?: string,
	isPrivate?: boolean

	displayName?: string,
	greeting?: string,
	systemPrompt?: string,
	hidePoweredBy?: boolean,

	primaryColor?: string,
}) => {
	const response = await apiClient.patch<IAgent>(`/orgs/${body.orgId}/agents/${body.id}`, body);
	return response.data;
}
export const deleteAgent = async (body: {orgId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.delete<IAgent>(`/orgs/${body.orgId}/agents/${body.id}`);
	return response.data;
}
export const publishAgent = async (body: {orgId: ObjectId, id:ObjectId, renewkey?: boolean}) => {
	const response = await apiClient.post<IAgent>(`/orgs/${body.orgId}/agents/${body.id}/publish`, body);
	return response.data;
}
export const unPublishAgent = async (body: {orgId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.post<IAgent>(`/orgs/${body.orgId}/agents/${body.id}/unpublish`);
	return response.data;
}
export const updateAgentAvatar = async (body: {orgId: ObjectId, id: ObjectId, formData: FormData}) => {
	const {data} = await apiClient.patch(`/orgs/${body.orgId}/agents/${body.id}/avatar`, body.formData, 
		{ headers: { 'Content-Type': 'multipart/form-data' } }
	);
	return data;
}
export const updateAgentLauncher = async (body: {orgId: ObjectId, id: ObjectId, formData: FormData}) => {
	const {data} = await apiClient.patch(`/orgs/${body.orgId}/agents/${body.id}/launcher`, body.formData, 
		{headers: { 'Content-Type': 'multipart/form-data' } }
	);
	return data;
}

// test data for predefined tools
const predefinedTools: Record<string, PredefinedTool> = {
	'search-kb': {
		id: "search-kb",
		name: "Search Knowledge Base",
		description: "Search through uploaded documents and knowledge base",
		enabled: true,
	},
	'collect-contact-info': {
		id: "collect-contact-info",
		name: "Collect Contact Information",
		description: "Collect and validate email addresses and phone numbers from users",
		enabled: false,
	},
};

// test data for API tools
const apiTools: Record<string, APIToolConfig> = {
	// 'weather-api': {
	// 	id: "weather-api",
	// 	name: "Weather API",
	// 	description: "Fetch current weather data",
	// 	method: "GET",
	// 	url: "https://api.weatherapi.com/v1/current.json",
	// 	headers: {
	// 		"Content-Type": { value: "application/json", required: true, type: 'system-value' }
	// 	},
	// 	params: {
	// 		key: { value: "", required: true, type: 'user-input', description: "API Key for Weather API" },
	// 		q: { value: "", required: true, type: 'user-input', description: "Location to get weather for" }
	// 	},
	// 	data: {},
	// 	responseType: "json",
	// 	enabled: true
	// },
	'translate-api': {
		id: "translate-api",
		name: "Translate API",
		description: "Translate text between languages",
		method: "POST",
		url: "https://api.example.com/translate",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			text: { value: "", required: true, type: 'user-input', description: "Text to translate" },
			targetLang: { value: "", required: true, type: 'user-input', description: "Target language code" }
		},
		responseType: "json",
		enabled: false
	},
	'email-sender': {
		id: "email-sender",
		name: "Email Sender",
		description: "Send emails to users",
		method: "POST",
		url: "https://api.example.com/send-email",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			to: { value: "", required: true, type: 'user-input', description: "Recipient email address" },
			subject: { value: "", required: true, type: 'user-input', description: "Email subject" },
			body: { value: "", required: true, type: 'user-input', description: "Email body content" }
		},
		responseType: "json",
		enabled: false
	},
	'authenticate-user': {
		id: "authenticate-user",
		name: "Authenticate User",
		description: "Authenticate a user with username and password",
		method: "POST",
		url: "https://api.example.com/authenticate",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			username: { value: "", required: true, type: 'user-input', description: "User's username" },
			password: { value: "", required: true, type: 'user-input', description: "User's password" }
		},
		responseType: "json",
		enabled: false
	},
	'send-password-reset-email': {
		id: "send-password-reset-email",
		name: "Send Password Reset Email",
		description: "Send a password reset email to the user",
		method: "POST",
		url: "https://api.example.com/password-reset",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			email: { value: "", required: true, type: 'user-input', description: "User's email address" }
		},
		responseType: "json",
		enabled: false
	},
	'check-order': {
		id: "check-order",
		name: "Check Order",
		description: "Check the status of an order",
		method: "GET",
		url: "https://api.example.com/orders/status",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {
			orderId: { value: "", required: true, type: 'user-input', description: "Order ID to check" }
		},
		data: {},
		responseType: "json",
		enabled: false
	},
	'escalate-to-human-agent': {
		id: "escalate-to-human-agent",
		name: "Escalate to Human Agent",
		description: "Escalate the conversation to a human agent",
		method: "POST",
		url: "https://api.example.com/escalate",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			conversationId: { value: "", required: true, type: 'user-input', description: "Conversation ID to escalate" }
		},
		responseType: "json",
		enabled: false
	},
	'correct-order-mistakes': {
		id: "correct-order-mistakes",
		name: "Correct Order Mistakes",
		description: "Correct mistakes in an order",
		method: "POST",
		url: "https://api.example.com/orders/correct",
		headers: {
			"Content-Type": { value: "application/json", required: true, type: 'system-value' }
		},
		params: {},
		data: {
			orderId: { value: "", required: true, type: 'user-input', description: "Order ID to correct" },
			corrections: { value: "", required: true, type: 'user-input', description: "Corrections to apply" }
		},
		responseType: "json",
		enabled: false
	},
};

// predefined tools
export const getAgentPredefinedTools = async (orgId: ObjectId, agentId: ObjectId): Promise<PredefinedTool[]> => {
	// mocked response - return list of predefined tools
	const tools = Object.values(predefinedTools)
	return tools;
	// const response = await apiClient.get<IAgent>(`/orgs/${orgId}/agents/${agentId}/tools`);
	// return response.data;
}
export const managePredefinedTool = async (body: {orgId: ObjectId, agentId: ObjectId, toolId: string, enabled: boolean}): Promise<PredefinedTool> => {

	// mocked response - update tool in predefinedTools
	if (predefinedTools[body.toolId]) {
		predefinedTools[body.toolId].enabled = body.enabled;
		return predefinedTools[body.toolId];
	}
	throw new Error(`Tool with ID ${body.toolId} not found in predefined tools.`);
	// const response = await apiClient.post<IAgent>(`/orgs/${body.orgId}/agents/${body.agentId}/tools/${body.toolId}/activate`);
	// return response.data;
}

// api tools
export const addApiTool = async (body: APIToolConfig): Promise<APIToolConfig> => {
	// mocked response - add tool to apiTools
	if (apiTools[body.id]) {
		throw new Error(`Tool with ID ${body.id} already exists.`);
	}
	apiTools[body.id] = body;
	return body;
	// const response = await apiClient.post<APIToolConfig>(`/orgs/${body.orgId}/agents/${body.agentId}/apiTools`, body);
	// return response.data;
}
export const updateApiTool = async (body: APIToolConfig): Promise<APIToolConfig> => {
	// mocked response - update tool in apiTools
	if (apiTools[body.id]) {
		apiTools[body.id] = body;
		return body;
	}
	throw new Error(`Tool with ID ${body.id} not found.`);
	// const response = await apiClient.patch<APIToolConfig>(`/orgs/${body.orgId}/agents/${body.agentId}/apiTools/${body.id}`, body);
	// return response.data;
}
export const getApiTools = async (orgId: ObjectId, agentId: ObjectId): Promise<APIToolConfig[]> => {
	// mocked response - return list of api tools
	const tools = Object.values(apiTools);
	return tools;
	// const response = await apiClient.get<APIToolConfig[]>(`/orgs/${orgId}/agents/${agentId}/apiTools`);
	// return response.data;
}
export const deleteApiTool = async (body: {orgId: ObjectId, agentId: ObjectId, toolId: string}): Promise<void> => {
	// mocked response - delete tool from apiTools
	if (apiTools[body.toolId]) {
		delete apiTools[body.toolId];
		return;
	}
	throw new Error(`Tool with ID ${body.toolId} not found.`);
	// const response = await apiClient.delete<APIToolConfig>(`/orgs/${body.orgId}/agents/${body.agentId}/apiTools/${body.toolId}`);
	// return response.data;
}


// textBlock
export const addTextBlock = async (body: {
	orgId: ObjectId, 
	agentId: ObjectId, 
	title: string, 
	text: string
}) => {	
	const response = await apiClient.post<ITextBlock>(`/orgs/${body.orgId}/agents/${body.agentId}/textBlocks`, body);
	return response.data;
}
export const getTextBlocks = async (body: { orgId: ObjectId, agentId: ObjectId}) => {
	const response = await apiClient.get<ITextBlock[]>(`/orgs/${body.orgId}/agents/${body.agentId}/textBlocks`);
	return response.data;
}
export const updateTextBlock = async (body: {
	orgId: ObjectId, 
	agentId: ObjectId,
	id: ObjectId,
	title: string, 
	text: string
}) => {
	const response = await apiClient.patch<ITextBlock>(`/orgs/${body.orgId}/agents/${body.agentId}/textBlocks/${body.id}`, body);
		return response.data;
}
export const deleteTextBlock = async (body: {
	orgId: ObjectId,
	agentId: ObjectId,
	id: ObjectId
}) => {
	const response = await apiClient.delete<ITextBlock>(`/orgs/${body.orgId}/agents/${body.agentId}/textBlocks/${body.id}`);
	return response.data;
}

// faq
export const addFaq = async (body: {orgId: ObjectId, agentId: ObjectId, question: string, answer : string}) => {
	const response = await apiClient.post<IFAQ>(`/orgs/${body.orgId}/agents/${body.agentId}/faqs`, body);
	return response.data;
}
export const getFaqs = async (body: { orgId: ObjectId, agentId: ObjectId}) => {
	const response = await apiClient.get<IFAQ[]>(`/orgs/${body.orgId}/agents/${body.agentId}/faqs`);
	return response.data;
}
export const updateFaq = async (body: {
	id: ObjectId, 
	question: string, 
	answer: string,
	agentId: ObjectId,
	orgId: ObjectId
}) => {
	const response = await apiClient.patch<IFAQ>(`/orgs/${body.orgId}/agents/${body.agentId}/faqs/${body.id}`, body);
	return response.data;
}
export const deleteFaq = async (body: {orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.delete<IFAQ>(`/orgs/${body.orgId}/agents/${body.agentId}/faqs/${body.id}`);
	return response.data;
}

//webLink
export const addWebLinks = async (body: {orgId: ObjectId, agentId: ObjectId, urls: string[]}) => {
	const response = await apiClient.post<IWebLink[]>(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks`, body);
	return response.data;
}
export const getWebLinks = async (body: {orgId: ObjectId, agentId: ObjectId}) => {
	const response = await apiClient.get<IWebLink[]>(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks`);
	return response.data;
}
export const refreshWebLink = async (body: {orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.put<IWebLink[]>(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks/${body.id}/refresh`);
	return response.data;
}
export const updateWebLink = async (body: {
	id: ObjectId,
	orgId: ObjectId,
	agentId: ObjectId,
	text: string,
}) => {
	const response = await apiClient.patch<IWebLink>(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks/${body.id}`, body);
	return response.data;
}
export const deleteWebLink = async (body: {
	orgId: ObjectId,
	agentId: ObjectId,
	id: string,
}) => {
	const response = await apiClient.delete<IWebLink>(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks/${body.id}`);
	return response.data;
}

// file
export const uploadFiles = async (body: {orgId: ObjectId, agentId: ObjectId, formData: FormData}) => {
	const response = await apiClient.post<IFile[]>(
		`/orgs/${body.orgId}/agents/${body.agentId}/files`, 
		body.formData,
		{
			headers: { 'Content-Type': 'multipart/form-data' }
		}
	);
	return response.data;
}
export const getFiles = async (body: {orgId: ObjectId, agentId: ObjectId}) => {
	const response = await apiClient.get<IFile[]>(`/orgs/${body.orgId}/agents/${body.agentId}/files`);
	return response.data;
}
export const updateFile = async (body: {
	id: ObjectId,
	orgId: ObjectId,
	agentId: ObjectId,
	text: string,
}) => {
	const response = await apiClient.patch<IFile>(`/orgs/${body.orgId}/agents/${body.agentId}/files/${body.id}`,body);
	return response.data;
}
export const deleteFile = async (body: {
	orgId: ObjectId,
	agentId: ObjectId,
	id: string,
}) => {
	const response = await apiClient.delete<IFile>(`/orgs/${body.orgId}/agents/${body.agentId}/files/${body.id}`);
	return response.data;
}

// initiate train model
export const trainModelWithTextBlock = async (body:{orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.put(`/orgs/${body.orgId}/agents/${body.agentId}/textBlocks/${body.id}`);
	return response.data
}
export const trainModelWithWebLink = async (body:{orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.put(`/orgs/${body.orgId}/agents/${body.agentId}/webLinks/${body.id}`);
	return response.data
}
export const trainModelWithFile = async (body:{orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.put(`/orgs/${body.orgId}/agents/${body.agentId}/files/${body.id}`);
	return response.data
}
export const trainModelWithFaq = async (body:{orgId: ObjectId, agentId: ObjectId, id: ObjectId}) => {
	const response = await apiClient.put(`/orgs/${body.orgId}/agents/${body.agentId}/faqs/${body.id}`);
	return response.data
}

// Chat bot
export const getChatTestResponse = async ( body: {
	orgId: ObjectId,
	agentId: ObjectId,
	chatHistory: IChatHistory[]
}) => {
	const response = await apiClient.post<IMessage>(`/orgs/${body.orgId}/agents/${body.agentId}/chat`, body);
	return response.data;
}
export const getChatAgent = async (accessKey:ObjectId) => {
	const response = await apiClient.get<IAgent>(`/chatagents/${accessKey}`);
	return response.data;
}
export const getChatResponse = async (body:{
	accessKey: string,
	sessionId: string
	message: IChatHistory
}) => {
	const response = await apiClient.post<IMessage>(`/chatagents/${body.accessKey}/chat`, body);
	return response.data;
}

// WhatsApp
export const addWhatsAppCrendentials = async (body: {apiKey: string, accessToken: string, phoneNumberId: string}) => {
	const response = await apiClient.post<{verifyToken: string}>(`/whatsapp/add/${body.apiKey}`, body);
	return response.data;
}

// activity
export const getConversations = async (body: { orgId:ObjectId, agentId: ObjectId, limit?: number, page?: number}) => {
	const response = await apiClient.get<{rows: IConversation[], count: number}>(`/orgs/${body.orgId}/agents/${body.agentId}/activity`, 
	{
		params: {
			limit: body.limit,
			page: body.page
		}
	});
	return response.data;
}
export const updatedConversation = async (body: {orgId: ObjectId, agentId: ObjectId, conversationId: ObjectId, status: ConversationStatus}) => {
	const response = await apiClient.patch<IConversation>(`/orgs/${body.orgId}/agents/${body.agentId}/conversations/${body.conversationId}`, body);
	return response.data;
}
export const getMarktingData = async (body: { orgId:ObjectId, agentId: ObjectId, dateRange: {fromDate: Date, toDate: Date}}) => {
	const response = await apiClient.get<{conversations: IConversation[], messages: IMessage[]}>(`/orgs/${body.orgId}/agents/${body.agentId}/messages`,
		{
			params: body.dateRange
		}
	);
	return response.data;
}